import { ActionItemParams, useTrackClickedActionItem } from '@air/marketing-analytics';
import { DemoDialog } from '@air/marketing-tech-debt/src/components/dialogs/DemoDialog';
import { BoxProps, Button, ButtonProps } from '@air/zephyr';
import { noop } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { forwardRef, MouseEvent, ReactNode, useState } from 'react';
import { UrlObject } from 'url';

interface ButtonLinkProps extends Omit<BoxProps<'a'>, 'type' | 'variant'>, Pick<ButtonProps, 'size' | 'variant'> {
  analytics?: ActionItemParams;
  type?: 'external' | 'internal';
}

/**
 * This component is used for the `buttonLinkRenderer` and should not be used outside of the scope of the function.
 * Do not export this component.
 */
const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ analytics, children, onClick, href, type, tx, ...restOfProps }: ButtonLinkProps, ref) => {
    const [isDialogOpened, setIsDialogOpened] = useState(false);
    const { asPath } = useRouter();
    const { trackClickedActionItem } = useTrackClickedActionItem();

    const isDemoDialog = href?.includes('#demo');

    const onButtonClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (onClick) onClick(event);

      trackClickedActionItem({
        destination: href,
        destinationType: type,
        location: window.location.href,
        pathName: asPath,
        zone: 'marketing',
        ...analytics,
      });
    };

    if (isDemoDialog) {
      return (
        <>
          <DemoDialog
            isOpen={isDialogOpened}
            onDismiss={() => {
              setIsDialogOpened(false);
            }}
          />
          <Button
            as="a"
            onClick={(event: MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault();
              onButtonClick(event);
              setIsDialogOpened(true);
            }}
            href={href}
            ref={ref}
            tx={{ ...tx }}
            {...restOfProps}
          >
            {children}
          </Button>
        </>
      );
    }

    return (
      <Button
        as="a"
        href={href}
        onClick={onButtonClick}
        ref={ref}
        tx={{ ...tx, '-webkit-appearance': 'none' }}
        {...restOfProps}
      >
        {children}
      </Button>
    );
  },
);

ButtonLink.displayName = 'ButtonLink';

export interface ButtonParam extends ButtonLinkProps {
  href: string | (string & UrlObject);
}

export type ButtonLinkRenderer = (button: ButtonParam) => ReactNode;

export const buttonLinkRenderer: ButtonLinkRenderer = ({ id, href, onClick = noop, type, ...restOfProps }) => {
  const isIntercom = href.includes('#intercom');
  const isExternalUrl = type === 'external';

  /**
   * Intercom button events are managed through the Intercom dashboard
   */
  if (isIntercom) {
    return (
      <Button
        className="intercom-button"
        id={`intercom-button-${id}`}
        onClick={onClick}
        key={id}
        type="button"
        {...restOfProps}
      />
    );
  }

  if (isExternalUrl) {
    return (
      <ButtonLink
        href={href}
        key={id}
        onClick={onClick}
        type={type}
        {...restOfProps}
        rel="noopener noreferrer"
        target="_blank"
      />
    );
  }

  return (
    <Link href={href} passHref key={id}>
      <ButtonLink type={type} {...restOfProps} />
    </Link>
  );
};
